/**
 * D-EZ-110 — THE CHAPTERS ON SCREEN: Silly Season, the rookie class, Hall of
 * Fame night and the preseason preview. Pure renderers of the domain's
 * chapters (`domain/chapters`) — every word on these pages was written there,
 * from the record; nothing here composes a sentence.
 */

import type { HallOfFameNight, PreseasonPreview, RookieClass, Said, SillySeason } from '../../domain/chapters/index.ts';
import type { ScoutingReport } from '../../domain/chapters/rookies.ts';

type OnDriver = ((driverId: string) => void) | undefined;

function Name({ id, name, onDriver }: { id: string; name: string; onDriver: OnDriver }) {
  if (!onDriver) return <>{name}</>;
  return (
    <button type="button" className="linkbtn" onClick={() => onDriver(id)}>
      {name}
    </button>
  );
}

export function Quote({ said, onDriver }: { said: Said | null | undefined; onDriver?: OnDriver }) {
  if (!said) return null;
  return (
    <blockquote className="chap__quote">
      <p>“{said.text}”</p>
      <footer>
        — <Name id={said.driverId} name={said.name} onDriver={onDriver} />
      </footer>
    </blockquote>
  );
}

// ---------------------------------------------------------------------------

export function SillySeasonView({ chapter, onDriver }: { chapter: SillySeason; onDriver?: OnDriver }) {
  return (
    <article className="chap chap--winter" aria-label={`Silly Season — the winter after season ${chapter.seasonNumber}`}>
      <header className="chap__head">
        <p className="chap__kicker">Silly Season · the winter after season {chapter.seasonNumber}</p>
        <h2 className="chap__headline">{chapter.headline.text}</h2>
        {chapter.dek && <p className="chap__dek">{chapter.dek.text}</p>}
      </header>
      <p className="chap__lede">{chapter.opening.map((t) => t.text).join(' ')}</p>
      <ul className="chap__counts" aria-label="The winter in numbers">
        <li><b>{chapter.counts.retired}</b> retired</li>
        <li><b>{chapter.counts.lostRide}</b> out of a ride</li>
        <li><b>{chapter.counts.moved}</b> changed shops</li>
        <li><b>{chapter.counts.movedUp}</b> moved up</li>
        <li><b>{chapter.counts.rookies}</b> rookies</li>
        {chapter.counts.sold > 0 && <li><b>{chapter.counts.sold}</b> charters sold</li>}
      </ul>
      {chapter.sections.map((sec) => (
        <section key={sec.key} className="chap__sec">
          <h3 className="chap__h">{sec.title}</h3>
          <div className="chap__stories">
            {sec.stories.map((st, i) => (
              <div key={`${st.kind}-${st.driverIds.join('-')}-${i}`} className={`chap__story chap__story--${st.kind}`}>
                <h4 className="chap__storyhead">{st.head.text}</h4>
                <p className="chap__body">{st.body.map((b) => b.text).join(' ')}</p>
                <Quote said={st.quote} onDriver={onDriver} />
              </div>
            ))}
          </div>
          {sec.alsoLine && <p className="chap__also">{sec.alsoLine.text}</p>}
        </section>
      ))}
      {chapter.hallClass.length > 0 && (
        <p className="chap__note">
          Going into the Hall of Fame before season {chapter.nextSeason}:{' '}
          {chapter.hallClass.map((h, i) => (
            <span key={h.driverId}>
              {i > 0 ? '; ' : ''}
              <b>{h.name}</b> ({h.citation})
            </span>
          ))}
          .
        </p>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------

function Meter({ label, value, standing }: { label: string; value: string | number; standing: number }) {
  const tone = standing >= 0.7 ? 'is-good' : standing <= 0.3 ? 'is-bad' : '';
  return (
    <div className={`rk__stat ${tone}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Report({ r, onDriver }: { r: ScoutingReport; onDriver: OnDriver }) {
  return (
    <li className="rk">
      <header className="rk__head">
        <span className="rk__rank" aria-label={`${r.classRank} in the class on paper`}>{r.classRank}</span>
        <div className="rk__who">
          <h4 className="rk__name">
            <Name id={r.driverId} name={r.name} onDriver={onDriver} /> <span className="rk__car">#{r.carNumber}</span>
          </h4>
          <p className="rk__meta">
            {r.team ?? 'Own car'} · {r.age}
            {r.hometown ? ` · ${r.hometown}` : ''}
          </p>
        </div>
      </header>
      <dl className="rk__card">
        <Meter label="Gas" value={r.card.gas} standing={r.standing.speed} />
        <Meter label="Brake" value={r.card.brake} standing={r.standing.defence} />
        <Meter label="Escape" value={r.card.escape} standing={r.standing.escape} />
        <Meter label="Quals" value={r.card.qualities.length} standing={r.standing.qualities} />
        <Meter label="Trophy" value={r.card.trophy ?? '—'} standing={r.standing.qualifying} />
      </dl>
      {r.strengths.length > 0 && (
        <ul className="rk__list rk__list--plus" aria-label="What the card likes">
          {r.strengths.map((s) => <li key={s.text}>{s.text}</li>)}
        </ul>
      )}
      {r.weaknesses.length > 0 && (
        <ul className="rk__list rk__list--minus" aria-label="What the card worries about">
          {r.weaknesses.map((s) => <li key={s.text}>{s.text}</li>)}
        </ul>
      )}
      <p className="rk__verdict">{r.verdict.text}</p>
      {(r.traits.length > 0 || r.backstory) && (
        <p className="rk__persona">
          {r.traits.join(' · ')}
          {r.backstory ? <><br />{r.backstory}</> : null}
        </p>
      )}
      <Quote said={r.quote} onDriver={onDriver} />
    </li>
  );
}

export function RookieClassView({ rookies, onDriver }: { rookies: RookieClass; onDriver?: OnDriver }) {
  return (
    <article className="chap chap--rookies" aria-label={`The rookie class for season ${rookies.raceSeason}`}>
      <header className="chap__head">
        <p className="chap__kicker">The rookie class · season {rookies.raceSeason}</p>
        <h2 className="chap__headline">Scouting reports</h2>
        <p className="chap__dek">{rookies.intro.text} Every number is off the card; the scout says what it says and guesses nothing.</p>
      </header>
      <ol className="rk__grid">
        {rookies.reports.map((r) => <Report key={r.driverId} r={r} onDriver={onDriver} />)}
      </ol>
    </article>
  );
}

// ---------------------------------------------------------------------------

export function HallOfFameNightView({ night, onDriver }: { night: HallOfFameNight; onDriver?: OnDriver }) {
  return (
    <article className="chap chap--hof" aria-label={`Hall of Fame night, season ${night.inductedSeason}`}>
      <header className="chap__head">
        <p className="chap__kicker">Hall of Fame night · season {night.inductedSeason}</p>
        <h2 className="chap__headline">{night.inductees.map((i) => i.name).join(' · ')}</h2>
        <p className="chap__dek">{night.intro.text}</p>
      </header>
      {night.inductees.map((i) => (
        <section key={i.driverId} className="hn">
          <h3 className="hn__name">
            <Name id={i.driverId} name={i.name} onDriver={onDriver} />
          </h3>
          <p className="hn__case">{i.case}</p>
          <dl className="hn__numbers">
            <div><dt>Starts</dt><dd>{i.numbers.starts}</dd></div>
            <div><dt>Wins</dt><dd>{i.numbers.wins}</dd></div>
            <div><dt>Titles</dt><dd>{i.numbers.championships}</dd></div>
            <div><dt>Top 10s</dt><dd>{i.numbers.top10}</dd></div>
            <div><dt>Poles</dt><dd>{i.numbers.poles}</dd></div>
          </dl>
          {i.intro.map((t) => <p key={t.text} className="chap__body">{t.text}</p>)}
          <blockquote className="chap__quote chap__quote--booth">
            <p>“{i.analyst.words.text}”</p>
            <footer>— {i.analyst.name}, {i.analyst.seat.toLowerCase()}</footer>
          </blockquote>
          <Quote said={i.rival} onDriver={onDriver} />
          <Quote said={i.speech} onDriver={onDriver} />
        </section>
      ))}
    </article>
  );
}

// ---------------------------------------------------------------------------

export function PreseasonPreviewView({ preview, onDriver }: { preview: PreseasonPreview; onDriver?: OnDriver }) {
  return (
    <article className="chap chap--preview" aria-label={preview.edition.text}>
      <header className="pv__head">
        <p className="pv__masthead">{preview.masthead}</p>
        <p className="pv__edition">{preview.edition.text}</p>
        <h2 className="chap__headline">{preview.headline.text}</h2>
        <p className="chap__dek">{preview.dek.text}</p>
      </header>
      <div className="pv__cols">
        {preview.sections.map((sec) => (
          <section key={sec.key} className={`pv__sec pv__sec--${sec.key}`}>
            <h3 className="chap__h">{sec.title}</h3>
            {sec.key === 'favourites' ? (
              <ol className="pv__favs">
                {preview.favourites.map((f) => (
                  <li key={f.driverId} className={`pv__fav pv__fav--${f.tier.replace(' ', '-')}`}>
                    <span className="pv__tier">{f.tier}</span>
                    <span className="pv__line">{f.line.text}</span>
                    {onDriver && (
                      <button type="button" className="linkbtn pv__card" onClick={() => onDriver(f.driverId)}>
                        card
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              sec.paragraphs.map((p, i) => <p key={`${i}-${p.text}`} className="chap__body">{p.text}</p>)
            )}
            {sec.quotes.map((q) => <Quote key={`${q.driverId}-${q.fact}`} said={q} onDriver={onDriver} />)}
          </section>
        ))}
      </div>
      <p className="chap__note">Written from last season and the winter only — nothing of the season ahead.</p>
    </article>
  );
}
